import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import TransactionsModal from '../BankAgent/TransactionsModal';
import DataQualityDisplay from '../BudgetAgent/DataQualityDisplay';
import CategoryReviewDisplay from '../BudgetAgent/CategoryReviewDisplay';
import BudgetSuggestionsDisplay from '../BudgetAgent/BudgetSuggestionsDisplay';

/**
 * CustomComponentRenderer - Renders custom React components in workflows
 *
 * Supports dynamic component loading and prop mapping from workflow context
 */
const CustomComponentRenderer = ({ definition, onClose, onSubmit, workflowContext }) => {
  const [componentData, setComponentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    component_name,
    component_path,
    props_mapping = {},
    metadata = {}
  } = definition;

  useEffect(() => {
    // Fetch data from workflow context if needed
    const fetchContextData = async () => {
      try {
        setLoading(true);

        // For bank workflows, fetch from bank_workflow_context
        if (workflowContext?.runId && workflowContext?.workflowId === 16) {
          console.log('🏦 Fetching bank workflow results for runId:', workflowContext.runId);

          const token = Cookies.get('sankalvax_token');
          const response = await fetch(
            `/api/bank/v2/workflows/results/${workflowContext.runId}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (!response.ok) {
            throw new Error('Failed to fetch workflow results');
          }

          const data = await response.json();
          console.log('✅ Bank workflow results fetched:', data);

          setComponentData(data);
        } else {
          // For other workflows, data might already be in workflowContext
          setComponentData(workflowContext);
        }

        setLoading(false);
      } catch (err) {
        console.error('❌ Error fetching component data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchContextData();
  }, [workflowContext]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-500 mb-2 text-2xl">⚠</div>
        <p className="text-gray-600 dark:text-gray-400">
          Error loading component: {error}
        </p>
        <button
          onClick={onClose}
          className="mt-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          Close
        </button>
      </div>
    );
  }

  // Map component names to actual components
  const componentMap = {
    'TransactionsModal': TransactionsModal,
    'DataQualityDisplay': DataQualityDisplay,
    'CategoryReviewDisplay': CategoryReviewDisplay,
    'BudgetSuggestionsDisplay': BudgetSuggestionsDisplay,
    // Add more custom components here as needed
  };

  const Component = componentMap[component_name];

  if (!Component) {
    return (
      <div className="p-6 text-center">
        <div className="text-yellow-500 mb-2 text-2xl">⚠</div>
        <p className="text-gray-600 dark:text-gray-400">
          Unknown component: {component_name}
        </p>
      </div>
    );
  }

  // Build props for the component based on props_mapping
  const componentProps = {
    onClose: () => {
      console.log('🔄 Custom component closed');
      if (onClose) onClose();
    },
    onConfirm: onSubmit ? () => {
      console.log('✅ Custom component confirmed');
      onSubmit({});
    } : undefined
  };

  // Map data from componentData to component props
  if (component_name === 'DataQualityDisplay' && componentData) {
    // Budget Agent - Data Quality Display
    componentProps.dataQuality = componentData.data_quality || {};
    componentProps.onConfirm = (data) => {
      console.log('📊 Budget DataQualityDisplay confirmed:', data);
      if (onSubmit) onSubmit(data);
    };
  }

  if (component_name === 'CategoryReviewDisplay' && componentData) {
    // Budget Agent - Category Review Display
    componentProps.categories = componentData.categories || [];
    componentProps.onConfirm = (data) => {
      console.log('📊 Budget CategoryReviewDisplay confirmed:', data);
      if (onSubmit) onSubmit(data);
    };
  }

  if (component_name === 'BudgetSuggestionsDisplay' && componentData) {
    // Budget Agent - Budget Suggestions Display
    console.log('📊 BudgetSuggestionsDisplay - componentData:', componentData);
    console.log('📊 BudgetSuggestionsDisplay - suggestions:', componentData.suggestions);
    console.log('📊 BudgetSuggestionsDisplay - currency:', componentData.base_currency);
    componentProps.suggestions = componentData.suggestions || [];
    componentProps.budgetName = componentData.budget_name || '';
    componentProps.fiscalYear = componentData.fiscal_year || '';
    componentProps.currency = componentData.base_currency || 'USD';
    componentProps.onConfirm = (data) => {
      console.log('📊 Budget BudgetSuggestionsDisplay confirmed:', data);
      if (onSubmit) onSubmit(data);
    };
  }

  if (component_name === 'TransactionsModal' && componentData) {
    componentProps.transactionsData = componentData.results || {};
    componentProps.bankAccount = componentData.bank_account || '';
    componentProps.bankName = componentData.bank_name || '';
    componentProps.currency = componentData.currency || '';
    componentProps.subsidiary = componentData.subsidiary || '';
    componentProps.runId = workflowContext?.runId || '';
    componentProps.workflowId = workflowContext?.workflowId || ''; // Add workflowId for child workflow creation
    componentProps.netsuiteAccountId = componentData.netsuite_account_id || ''; // NetSuite account for URL generation
    componentProps.isLiveWorkflow = true; // Mark as live workflow (not from history)
    // Add onRefresh to re-fetch results after transfer to JE
    componentProps.onRefresh = async () => {
      console.log('🔄 Refreshing TransactionsModal data...');
      try {
        const token = Cookies.get('sankalvax_token');
        const response = await fetch(
          `/api/bank/v2/workflows/results/${workflowContext.runId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Refreshed bank workflow results:', data);
          setComponentData(data);
        }
      } catch (err) {
        console.error('❌ Error refreshing data:', err);
      }
    };
  }

  console.log('🎨 Rendering custom component:', component_name, 'with props:', Object.keys(componentProps));

  return <Component {...componentProps} />;
};

export default CustomComponentRenderer;
